import { useState } from "react";

import { Box, Button, Stack, Typography } from "@mui/material";
import axios from "axios";

import { ConfirmForgotPassword } from "components/Authentication/components/ConfirmForgotPassword";
import { CarrierPreferences } from "components/Carrier/components/CarrierPreferences";
import { CreateAPIKeyForUserMenu } from "components/CreateAPIKeyForUserMenu";
import { EvotruxDivider } from "components/EvotruxDivider";
import { MFASetupSection } from "components/MFASetupSection";
import { ShipperEmailNotificationPreferences } from "components/Shipper/components/ShipperEmailNotificationPreferences";
import { useAuthContext, useRoleContext } from "hooks";
import { PageLayout } from "layouts";
import { formatPhoneNumber, ISOStringToShorthandDate } from "utils";

export function UserAccountPage() {
  const user = useAuthContext();
  const { role: userRole } = useRoleContext();
  const [showCodeSent, setShowCodeSent] = useState<boolean>(false);
  const [changeSuccess, setChangeSuccess] = useState<boolean>(false);

  if (user.user == null) {
    return (
      <PageLayout>
        <Typography color="red" align="center" variant="h4">
          {" "}
          Error loading user information - please contact us if this error
          persists
        </Typography>
      </PageLayout>
    );
  }

  const {
    email,
    first_name: firstName,
    last_name: lastName,
    work_phone_number: workPhoneNumber,
  } = user.user;

  const handleSendCode = () => {
    if (user.user != null) {
      axios.post(`${import.meta.env.REACT_APP_API_URL}/forgot-password`, {
        email,
      });
    }
  };

  return (
    <PageLayout>
      <Typography variant="h4" fontWeight={600} mb="2rem">
        Account Details
      </Typography>
      <Stack direction="column">
        <Box m="1rem">
          <Typography variant="h4">User Information</Typography>
          <EvotruxDivider />
          <Box mt="1rem">
            <Typography variant="h6">
              {firstName} {lastName}
            </Typography>
            <Typography variant="h6">{email}</Typography>
            {workPhoneNumber && (
              <Typography variant="h6">
                {formatPhoneNumber(workPhoneNumber.phone_number)}
                {workPhoneNumber.extension &&
                  ` ext. ${workPhoneNumber.extension}`}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Joined evotrux on {ISOStringToShorthandDate(user.user.created_at)}
            </Typography>
          </Box>
        </Box>
        <CarrierPreferences />
        <ShipperEmailNotificationPreferences />
        <Box m="1rem">
          <Typography variant="h4">Change Password</Typography>
          <EvotruxDivider />
          <Stack direction="column">
            <Typography>
              To change your password follow these instructions:
            </Typography>
            <ol>
              <li>
                <Typography>
                  Click &apos;SEND CODE TO EMAIL&apos; button.
                </Typography>
              </li>
              <li>
                <Typography>
                  Check your email for the code we sent you.
                </Typography>
              </li>
              <li>
                <Typography>
                  Enter the code in the menu below along with your new password.
                </Typography>
              </li>
            </ol>
            <Box mt="1rem" ml=".5rem">
              <Button
                variant="contained"
                onClick={() => {
                  handleSendCode();
                  setShowCodeSent(true);
                }}
              >
                Send Code to email
              </Button>
            </Box>

            {showCodeSent && (
              <Typography variant="h6" color="green" ml="1rem">
                Code sent!
              </Typography>
            )}
          </Stack>
          <Box p="1rem">
            <ConfirmForgotPassword
              email={email}
              backToLogin={false}
              onSuccessFromAccountPage={setChangeSuccess}
            />
            {changeSuccess && (
              <Typography variant="h6" color="green" align="center">
                Password changed successfully!
              </Typography>
            )}
          </Box>
        </Box>
        <Box m="1rem">
          <Typography variant="h4">Multi-Factor Authentication</Typography>
          <EvotruxDivider />
          <MFASetupSection />
        </Box>
        {userRole?.type === "SHIPPER" && (
          <Box ml="1rem" mr="1rem">
            <Typography variant="h4">Evotrux API</Typography>
            <EvotruxDivider />
            <CreateAPIKeyForUserMenu />
          </Box>
        )}
      </Stack>
    </PageLayout>
  );
}
